import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Plus, Edit, Trash2, User, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RichTextEditor from '../../components/ui/rich-text-editor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_GROUPS = ['All Ages', 'Adults', 'Youth', 'Children', 'Seniors'];

export default function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schedule: '',
    image: '',
    age_group: 'All Ages',
    contact_person: '',
    contact_email: '',
    is_active: true,
    is_visible: true
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/programs`);
      setPrograms(response.data);
    } catch (error) {
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProgram) {
        await axios.put(`${API}/programs/${editingProgram.id}`, formData);
        toast.success('Program updated');
      } else {
        await axios.post(`${API}/programs`, formData);
        toast.success('Program created');
      }
      setShowDialog(false);
      resetForm();
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to save program');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this program?')) return;
    try {
      await axios.delete(`${API}/programs/${id}`);
      toast.success('Program deleted');
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  const openEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name || '',
      description: program.description || '',
      schedule: program.schedule || '',
      image: program.image || '',
      age_group: program.age_group || 'All Ages',
      contact_person: program.contact_person || '',
      contact_email: program.contact_email || '',
      is_active: program.is_active !== false,
      is_visible: program.is_visible !== false
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingProgram(null);
    setFormData({
      name: '',
      description: '',
      schedule: '',
      image: '',
      age_group: 'All Ages',
      contact_person: '',
      contact_email: '',
      is_active: true,
      is_visible: true
    });
  };

  const getAgeGroupColor = (age) => {
    const colors = {
      'All Ages': 'bg-gold/10 text-gold',
      'Adults': 'bg-burgundy/10 text-burgundy',
      'Youth': 'bg-blue-100 text-blue-600',
      'Children': 'bg-pink-100 text-pink-600',
      'Seniors': 'bg-purple/10 text-purple'
    };
    return colors[age] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-programs-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Programs</h1>
          <p className="text-charcoal/60">Manage ministry programs</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-burgundy hover:bg-purple text-white"
          data-testid="add-program-btn"
        >
          <Plus size={18} className="mr-2" /> Add Program
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {programs.map((program) => (
          <motion.div
            key={program.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 hover:border-gold/30 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                {program.image ? (
                  <img src={program.image} alt={program.name} className="w-16 h-16 object-cover rounded-xl" />
                ) : (
                  <div className="w-16 h-16 bg-olive/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BookOpen size={28} className="text-olive" />
                  </div>
                )}
                <div>
                  <h3 className="font-serif text-lg text-charcoal">{program.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getAgeGroupColor(program.age_group)}`}>
                      {program.age_group}
                    </span>
                    {!program.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Inactive</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(program)} className="p-2 text-charcoal/50 hover:text-burgundy">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(program.id)} className="p-2 text-charcoal/50 hover:text-red-500">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            {program.schedule && (
              <p className="text-sm text-charcoal/60 mb-2">{program.schedule}</p>
            )}
            {program.contact_person && (
              <div className="flex items-center gap-4 text-sm text-charcoal/50 mt-3 pt-3 border-t border-gold/10">
                <span className="flex items-center gap-1">
                  <User size={14} /> {program.contact_person}
                </span>
                {program.contact_email && (
                  <span className="flex items-center gap-1">
                    <Mail size={14} /> {program.contact_email}
                  </span>
                )}
              </div>
            )}
          </motion.div>
        ))}
        {programs.length === 0 && (
          <div className="col-span-2 text-center py-12 text-charcoal/50">
            <BookOpen size={48} className="mx-auto mb-4 opacity-20" />
            <p>No programs yet. Create your first program!</p>
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingProgram ? 'Edit Program' : 'Create Program'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Program Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Age Group</Label>
                <Select value={formData.age_group} onValueChange={(v) => setFormData({...formData, age_group: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_GROUPS.map(age => (
                      <SelectItem key={age} value={age}>{age}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Schedule</Label>
                <Input
                  value={formData.schedule}
                  onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                  placeholder="e.g., Sundays at 9am"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Image URL</Label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({...formData, image: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Program description..."
                height="150px"
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_visible"
                  checked={formData.is_visible}
                  onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
                />
                <Label htmlFor="is_visible">Visible on website</Label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-burgundy hover:bg-purple">
              {editingProgram ? 'Update Program' : 'Create Program'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
